import { computed, reactive, ref, toValue, watch } from 'vue';
/**
 * The column list of the "edit columns" menu: the columns it offers, the order they are dragged
 * into and the titles that had to be clipped.
 *
 * @param columns All columns of the data grid, in the order they are displayed in.
 * @param reorderColumns Called with the data fields in their new order after a column was dragged.
 */
export function useColumnVisibilityMenu(columns, reorderColumns) {
    /**
     * Only columns the user is allowed to hide are listed, and only those can be reordered here.
     * A column without a title is left out, some modules declare one just to carry a filter.
     */
    const toggleableColumns = computed(() => (toValue(columns) ?? [])
        .filter(column => column.canBeToggled && column.title));
    /**
     * Dragging moves the entries around in this list, so it has to be a copy, a prop cannot be changed
     * from here. Once the grid has applied the new order, the copy is taken again.
     */
    const draggableColumns = ref([...toggleableColumns.value]);
    watch(toggleableColumns, columns => draggableColumns.value = [...columns]);
    /**
     * The datagrid without columns shows nothing at all, so the last column on display cannot be hidden.
     * Its entry stays checked but is disabled.
     */
    function isLastVisibleColumn(column) {
        return !column.isHidden && toggleableColumns.value.filter(entry => !entry.isHidden).length === 1;
    }
    /**
     * Titles that do not fit into the list and are therefore shown as a tooltip.
     */
    const clippedTitles = reactive({});
    /**
     * Hands the new order to the data grid after a column was dragged somewhere else.
     */
    function onColumnDragged(event) {
        if (event.oldIndex === event.newIndex)
            return;
        const orderedDataFields = toggleableColumns.value.map(column => column.value);
        orderedDataFields.splice(event.newIndex, 0, orderedDataFields.splice(event.oldIndex, 1)[0]);
        reorderColumns(orderedDataFields);
    }
    /**
     * Remembers whether a column's title is clipped, so that the tooltip only appears if it adds something.
     */
    function checkTitleIsClipped(event, column) {
        const title = event.currentTarget?.querySelector('.column-title');
        if (!title)
            return;
        clippedTitles[column.value] = title.scrollWidth > title.clientWidth;
    }
    return { draggableColumns, clippedTitles, isLastVisibleColumn, onColumnDragged, checkTitleIsClipped };
}
